/**
 * What a press, a drag and a resize on the calendar do — apart from the page,
 * so the rules can be tested on their own (Aaron, Oct 23: the calendar works
 * like Outlook's — things go day to day and person to person, an edge
 * resizes, and the whole day is on the grid).
 */
import type { CalendarAnswer, CalendarItem } from '@rowted/shared'

/** Where a drag lands: a column's day and whose column it is. */
export interface DropTarget { date: string; personId: string }

/** A press this close to a block's top or bottom edge resizes it. */
export const EDGE_PX = 6

/** The shortest a block can be resized to. */
export const MIN_MINUTES = 15

const DAY_MINUTES = 24 * 60

/** Times on the grid move in fives. */
export const snap5 = (minutes: number): number => Math.round(minutes / 5) * 5

/**
 * What a press on a block at `y` does: on its bottom edge it moves the end,
 * on its top edge the start, anywhere else it moves the block. A block too
 * short to have a top edge and a middle keeps only the bottom one.
 */
export function pressPart(y: number, top: number, bottom: number): 'start' | 'end' | 'move' {
  if (y >= bottom - EDGE_PX) return 'end'
  if (y <= top + EDGE_PX && bottom - top > EDGE_PX * 3) return 'start'
  return 'move'
}

/** A dragged start: snapped to five minutes and kept inside the day. */
export function movedStart(minutes: number, duration: number): number {
  return Math.min(DAY_MINUTES - duration, Math.max(0, snap5(minutes)))
}

/** An edge dragged `delta` minutes: never shorter than 15 minutes, never off the day. */
export function resized(
  span: { start: number; end: number }, edge: 'start' | 'end', delta: number,
): { start: number; end: number } {
  const step = snap5(delta)
  return edge === 'end'
    ? { start: span.start, end: Math.min(DAY_MINUTES, Math.max(span.start + MIN_MINUTES, span.end + step)) }
    : { start: Math.max(0, Math.min(span.end - MIN_MINUTES, span.start + step)), end: span.end }
}

/**
 * Why a dragged item cannot go where it was dropped — the sentence the page
 * shows — or null when it can. A time block is its person's; a thing for
 * several people is changed in its sheet, because the calendar only knows the
 * people this viewer may see, so replacing them from here could drop somebody
 * (Aaron, Oct 24, small Q4: keep it that way); and a repeating one keeps its
 * person — *Just this one* moves a time or a day, not who it is for. Anything
 * else repeating moves, asking *Just this one · Every one* first (small Q3).
 */
export function dropRefusal(
  item: Pick<CalendarItem, 'kind' | 'title' | 'repeatRule' | 'people'>, from: DropTarget, to: DropTarget,
): string | null {
  if (to.personId !== from.personId) {
    if (item.kind === 'block') return 'A time block is its person’s own — it can’t move to someone else.'
    if (item.people.length > 1) return `${item.title} is for several people — open it to change who’s going.`
    if (item.repeatRule) return `${item.title} repeats — open it to change who it’s for.`
  }
  return null
}

/** A repeating item asks *Just this one · Every one* before a drag or a resize is written (small Q3). */
export function asksWhich(item: Pick<CalendarItem, 'repeatRule'>): boolean {
  return Boolean(item.repeatRule)
}

/** The occurrence a change names: its own day in the series, as the calendar was handed it. */
export function occurrenceOf(item: Pick<CalendarItem, 'occurrence' | 'date'>): string {
  return item.occurrence ?? item.date
}

/**
 * Where the hours open: the remembered start hour, or the hour of anything on
 * the screen that starts earlier — the grid holds the whole day, but nobody
 * should open it on midnight.
 */
export function openingMinutes(answer: Pick<CalendarAnswer, 'people'> | null, startHour: number): number {
  let first = startHour * 60
  for (const person of answer?.people ?? []) {
    for (const day of person.days) {
      for (const item of day.items) {
        if (!item.allDay) first = Math.min(first, Math.floor(item.start / 60) * 60)
      }
    }
  }
  return Math.max(0, first)
}
